import productShortDescription from '../utils/shortDescription';
import { discountCardFields } from '../services/discountBadge';
import { publicHref } from '../config/deployment';
/**
 * Map a CMS "product_carousel" widget response → ProductsGridBlock items array.
 *
 * @param {object|null} widget — full widget JSON returned by useWidget()
 * @param {object} [options]
 * @param {function} [options.navigate] — React Router navigate function for SPA navigation
 * @param {string}   [options.productBasePath] — base path for product details (e.g. '/clothing/cls-dk/product-details')
 * @param {string}   [options.packageBasePath] — base path for package details (e.g. '/clothing/cls-dk/product-package')
 * @param {string}   [options.schoolStorageKey] — localStorage key for school selection; when set, clicks check if school is selected
 * @param {boolean}  [options.toSchoolShop] — with menuKey + onMenuItemClick: clicks resolve to the customer's school shop / the item there
 * @returns {Array|null} items array ready for <ProductsGridBlock items={…} />, or null if data is unavailable
 */
export function mapProductCarouselItems(widget, options = {}) {
  if (!widget?.data?.items?.length) return null;

  const {
    navigate, productBasePath, packageBasePath, schoolStorageKey,
    // CLS-DK landing: resolve the click to the customer's own school shop and
    // the clicked product / package there (same flow as the collection cards).
    menuKey, onMenuItemClick, toSchoolShop = false,
  } = options;

  return widget.data.items.map((item) => {
    const mapped = {
      id: item.id,
      image: item.image,
      imageAlt: item.imageAlt || item.title,
      secondImage: item.secondImage || null,
      secondImageAlt: item.secondImageAlt || item.title,
      title: item.title,
      description: productShortDescription(item) ?? item.description ?? '',
      price: item.price,
      colors: item.colors || [],
    };

    // Build navigation: prefer SPA navigate(), fall back to <a href>
    const slug = item.slug || '';
    const isPackage = item.href?.startsWith('/packages/');
    const basePath = isPackage ? packageBasePath : productBasePath;

    // What the card IS — used by the click (services/shopDestination) and by the price
    // overlay (services/shopCardPrices: the visitor's school-shop price for this card).
    const target = isPackage
      ? { type: 'package', id: null, slug, name: item.title }
      : { type: 'product', id: null, productId: item.id ?? null, slug, name: item.title };
    mapped.match = target;

    // A real link to the card's page for crawlers and Ctrl/Cmd clicks; a plain click
    // still runs the onClick below (ProductCard).
    if (basePath && slug) mapped.href = publicHref(`${basePath}/${slug}`);

    if (toSchoolShop && onMenuItemClick && menuKey) {
      mapped.onClick = () => onMenuItemClick(menuKey, { toSchoolShop: true, target });
    } else if (navigate && basePath && slug) {
      const targetPath = `${basePath}/${slug}`;
      if (schoolStorageKey) {
        mapped.onClick = () => {
          if (localStorage.getItem(schoolStorageKey)) {
            navigate(targetPath);
          } else {
            document.dispatchEvent(new CustomEvent('open-school-selector', { detail: { navigateTo: targetPath } }));
          }
        };
      } else {
        mapped.onClick = () => navigate(targetPath);
      }
    } else if (!mapped.href) {
      mapped.href = item.href || '#';
    }

    return mapped;
  });
}

/**
 * Map a menu-item listing (from useMenuItemListing → { products, packages })
 * into ProductsGrid items. Products first, then packages.
 *
 * Click behaviour matches the landing's gated flow: when the brand's school
 * selector is enabled (e.g. CLS-DK) a click opens the school popup; when it's
 * disabled (CLS-SE / ABC-SE) the click goes straight to the bare configurator
 * (product-details / product-package by slug).
 *
 * With the brand's open shop DISABLED (`openShopEnabled: false`) a card click
 * must not reach the school-less configurator — it resolves exactly like a
 * header menu-item click instead (pass `menuKey` + `onMenuItemClick` from
 * useMenuItemNavigation): school → that menu item's school shop; no mapped
 * shop / no school → popup or the closed page per the brand's selector flag.
 *
 * @param {object|null} listing — { products: [], packages: [] }
 * @param {object} options
 * @param {string}   options.feSlug — route slug (e.g. 'cls-dk')
 * @param {function} options.navigate — React Router navigate
 * @param {boolean}  options.showSchoolSelector — brand popup flag
 * @param {boolean}  [options.openShopEnabled=true] — brand open-shop flag
 * @param {string}   [options.menuKey] — 'menu_1' | 'menu_2' of the block
 * @param {function} [options.onMenuItemClick] — useMenuItemNavigation resolver
 * @param {string}   [options.packageDescription] — subtitle for package cards
 * @returns {Array} items array for <ProductsGrid items={…} /> (empty if none)
 */
export function mapMenuItemListingCards(listing, options = {}) {
  const {
    feSlug, navigate, showSchoolSelector = false, openShopEnabled = true,
    menuKey, onMenuItemClick, packageDescription = '',
    // Append ?variation={collectionItemId} to product links so the bare PDP
    // shows this collection item (color subset, tier price, descriptions).
    variationLinks = true,
    // 'products' | 'packages' — a landing section showing one kind only.
    only = null,
    // true (CLS-DK landing): EVERY click resolves to the customer's own school
    // shop via onMenuItemClick(menuKey, {toSchoolShop, target}) — the clicked
    // item there when the shop sells it, else the shop page / any open shop /
    // the closed page; without a school the popup opens carrying the intent.
    toSchoolShop = false,
  } = options;
  const products = Array.isArray(listing?.products) ? listing.products : [];
  const packages = Array.isArray(listing?.packages) ? listing.packages : [];

  const makeClick = (targetPath, target = null) => () => {
    if (toSchoolShop && onMenuItemClick && menuKey) {
      onMenuItemClick(menuKey, { toSchoolShop: true, target });
    } else if (!openShopEnabled && onMenuItemClick && menuKey) {
      // Open shop disabled: resolve like the header menu item (school shop /
      // popup / closed page) — never the bare configurator.
      onMenuItemClick(menuKey);
    } else if (showSchoolSelector) {
      // Popup enabled (CLS-DK): pick a school first — same as the landing today.
      document.dispatchEvent(new CustomEvent('open-school-selector'));
    } else if (navigate && targetPath) {
      // Popup disabled (CLS-SE / ABC-SE): open the configurator directly.
      navigate(targetPath);
    }
  };

  const productPath = (p) => `/clothing/${feSlug}/product-details/${encodeURIComponent(p.slug)}`
    + (variationLinks && p.id ? `?variation=${encodeURIComponent(p.id)}` : '');
  const packagePath = (p) => `/clothing/${feSlug}/product-package/${encodeURIComponent(p.slug)}`;

  // `href` = the card's own page: a real link for crawlers and Ctrl/Cmd clicks; a plain
  // click runs `onClick` (ProductCard), which may resolve to the visitor's school shop.
  const productCards = products
    .filter((p) => p.slug)
    .map((p) => ({
      id: p.id,
      image: p.image || undefined,
      imageAlt: p.name,
      secondImage: p.secondImage || undefined,
      secondImageAlt: p.secondImage ? p.name : undefined,
      title: p.name,
      description: productShortDescription(p),
      price: p.price,
      // Crossed-out original + localized badge while the variation's discount is charged.
      ...discountCardFields(p, feSlug),
      // Swatches: the collection's colours of the product (the API's `colors`).
      colors: Array.isArray(p.colors) ? p.colors : [],
      match: { type: 'product', id: p.id, productId: p.productId ?? null, slug: p.slug, name: p.name },
      href: publicHref(productPath(p)),
      onClick: makeClick(
        productPath(p),
        { type: 'product', id: p.id, productId: p.productId ?? null, slug: p.slug, name: p.name },
      ),
    }));

  const packageCards = packages
    .filter((p) => p.slug)
    .map((p) => ({
      id: `pkg-${p.id}`,
      image: p.image || undefined,
      imageAlt: p.name,
      title: p.name,
      description: productShortDescription(p) ?? packageDescription ?? undefined,
      price: p.price,
      match: { type: 'package', id: p.id, slug: p.slug, name: p.name },
      href: publicHref(packagePath(p)),
      onClick: makeClick(
        packagePath(p),
        { type: 'package', id: p.id, slug: p.slug, name: p.name },
      ),
    }));

  if (only === 'products') return productCards;
  if (only === 'packages') return packageCards;
  return [...productCards, ...packageCards];
}
